import { Box } from '@factorialco/deprecated-design-system'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import legalEntities from 'modules/core/store/collections/legalEntities'

import FolderItem from 'modules/documents/components/DocumentsAndFolders/FoldersList/FolderItem'
import GridList from 'modules/documents/components/GridList'
import FoldersSkeleton from 'modules/documents/components/GridList/Skeleton'
import { useGetEmployeeFolder } from 'modules/documents/hooks/useGetEmployeeFolder'
import { useGetFolders } from 'modules/documents/hooks/useGetFolders'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { getSubfoldersCountForSkeletons, isCompanySpace } from 'modules/documents/lib/Documents'
import { sortFoldersByName } from 'modules/documents/lib/Folders'
import { SPACES } from 'modules/documents/lib/constants'
import type Employee from 'modules/employees/store/models/Employee'

import { filterByLegalEntityCountry, showByLegalEntity } from './legalEntityFolderFilter'

interface Props {
  employee?: Employee
  parentFolderId?: number
  space: (typeof SPACES)[keyof typeof SPACES]
  readonly: boolean
  onFolderSelected: (folder: Folder) => void
}

const FoldersList = ({ employee, parentFolderId, space, readonly, onFolderSelected }: Props) => {
  const isCompanySpaceCheck = isCompanySpace(space)
  const employeeId = employee ? employee.attributes.id : undefined
  const numberOfSubfolders = getSubfoldersCountForSkeletons(parentFolderId)

  const { loading: loadingFolders, folders } = useGetFolders(
    {
      parentFolderId,
      rootFolders: !parentFolderId,
      space,
      employeeId,
      withCounters: true,
    },
    'no-cache'
  )

  const { loading: loadingEmployeeFolders, employeeFolders } = useGetEmployeeFolder([employeeId!])

  let filteredFolders = folders

  if (!parentFolderId && !isCompanySpaceCheck) {
    filteredFolders = folders.filter((folder) =>
      employeeFolders?.some((employeeFolder) => employeeFolder?.folderId === folder.id)
    )
  }

  const { loading: loadingLegalEntities } = useAsyncData(async () => {
    await legalEntities.fetch({
      data: {
        employee_id: employeeId,
      },
      remove: false,
    })
  }, [employeeId])

  const legalEntity = legalEntities.find({ id: employee && employeeLegalEntityId(employee) })

  filteredFolders =
    employee && legalEntity
      ? filteredFolders.filter(({ identifier }) => {
          if (!identifier || !showByLegalEntity(identifier)) return true
          return filterByLegalEntityCountry(legalEntity.country, identifier)
        })
      : filteredFolders
  const sortedFolders = sortFoldersByName(filteredFolders)

  const loading = loadingFolders || loadingLegalEntities || loadingEmployeeFolders

  return (
    <Box>
      {loading && numberOfSubfolders > 0 && (
        <FoldersSkeleton numberOfSkeletons={numberOfSubfolders} />
      )}
      {!loading && (
        <GridList.List>
          {sortedFolders.map((folder) => (
            <FolderItem
              key={folder.id}
              employeeId={employeeId}
              parentFolderId={parentFolderId}
              folder={folder}
              readonly={readonly}
              onFolderSelected={onFolderSelected}
            />
          ))}
        </GridList.List>
      )}
    </Box>
  )
}

export default FoldersList
